import { SchulungsBand } from './SchulungsBand';
import type { ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { BottomNav } from './BottomNav';
import { AblageLeiste } from './Ablage';
import { useIstMobil } from '@/hooks/useLayout';
import { useRahmenVerankerung } from '@/hooks/useRahmenVerankerung';
import { useAkzentFarbe } from '@/hooks/useAkzentFarbe';

export function PageLayout({ children }: { children: ReactNode }) {
  const istMobil = useIstMobil();

  // Färbt die Oberfläche auf den angemeldeten Benutzer ein. Steht hier und
  // nicht in der Wurzelroute, weil Anmeldeseite und Druckansicht PageLayout
  // nicht rendern — beide sollen bei Teal bleiben.
  useAkzentFarbe();

  // Nimmt dem Wurzelelement das Scrollen, solange der Mobil-Rahmen gilt,
  // und räumt auf, wenn die Bildschirmtastatur zugeht (K7).
  useRahmenVerankerung(istMobil);

  // Zwei Rahmen, immer nur einer im Baum. Warum nicht beide rendern und
  // einen per CSS wegblenden, steht in hooks/useLayout.ts: die Tour sucht
  // ihre Anker im Dokument, nicht im Sichtbaren.
  if (istMobil) {
    return (
      // `100dvh` statt `100vh`: auf dem iPhone wächst und schrumpft die
      // Adressleiste, und `vh` rechnet mit dem GRÖSSTEN Fenster. Die
      // Bottom-Navigation läge damit zeitweise unter dem sichtbaren Rand.
      //
      // Sie ist bewusst ein normales Kind dieser Spalte und nicht
      // `fixed`: gescrollt wird im <main> darüber, die Leiste steht
      // ohnehin still. Als festes Element bräuchte sie eine Polsterung
      // im Inhalt, die exakt ihrer Höhe entspricht — und beim ersten
      // Anlauf klaffte dort genau deshalb ein leerer Streifen unter der
      // Zählzeile des Dashboards.
      <div className="flex h-[100dvh] flex-col">
        <SchulungsBand />
        <Topbar variante="mobil" />
        <main className="min-h-0 flex-1 overflow-y-auto" data-scroll-restoration-id="hauptbereich">{children}</main>
        <AblageLeiste />
        <BottomNav />
      </div>
    );
  }

  return (
    // `h-screen` statt `min-h-screen`, und `min-h-0` am <main> (A10.2/A10.7)
    //
    // Vorher wuchs diese Spalte mit ihrem Inhalt. Damit bekam <main> nie
    // eine begrenzte Hoehe, sein `overflow-y-auto` lief ins Leere, und
    // gerollt wurde das Fenster. Gemessen: nach 1345 px Rollen stand der
    // Spaltenkopf der Tabelle 1218 px ueber dem Fensterrand, obwohl
    // `sticky top-0` an ihm hing — er klebte an einem Kasten, der selbst
    // mitrollte.
    //
    // Der Mobil-Rahmen oben macht es seit jeher richtig (`h-[100dvh]` +
    // `min-h-0 flex-1`); hier fehlte es. Mit begrenzter Hoehe rollt der
    // Inhalt IN <main>, und was darin `sticky` oder `shrink-0` traegt,
    // bleibt wirklich stehen: Spaltenkopf oben, Fusszeile unten.
    //
    // `min-h-0` ist dabei kein Beiwerk: ein Flex-Kind darf von sich aus
    // nicht kleiner werden als sein Inhalt, und ohne diese Zeile waere
    // <main> wieder so hoch wie die ganze Liste.
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <SchulungsBand />
        <Topbar />
        <main className="min-h-0 flex-1 overflow-y-auto" data-scroll-restoration-id="hauptbereich">{children}</main>
        {/* Die Ablage (02.11.2026): unter dem Inhalt, nicht darüber —
            sie ist ein Weg zurück, kein Teil der Seite. */}
        <AblageLeiste />
      </div>
    </div>
  );
}
